import { Container, Graphics, Text, TextStyle } from "pixi.js";
import { pitchClassColor } from "@/lib/music/pitch-colors";
import { NoteEntry } from "./notes";

type HarmonyProps = {
  currentTick: number;
  currentSeconds: number;
  tickToSeconds: (tick: number) => number;
  fromTick: number;
  toTick: number;
  width: number;
  height: number;
  playheadY: number;
  hints: HintRow[];
}

/**
 * A single label, either a chord-tone degree or a non-chord note name.
 */
export type HintLabel = {
  /** Display text: degree ("1", "♭3") for chord tones, note name ("D5", "E5") for non-chord */
  text: string;
  /** "bright" for chord tones, "muted" for non-chord tones */
  style: 'bright' | 'muted';
};

/**
 * Notes belonging to one hand (lane) within a chord.
 */
export type HandNotes = {
  /** Lane index in the piano roll */
  laneIdx: number;
  /** Sorted MIDI note numbers for this hand */
  midis: number[];
  /** Mixed chord-tone degrees and non-chord note names, sorted by MIDI pitch (low→high), deduplicated */
  labels: HintLabel[];
};

export type HintRow = {
  /** Representative tick position */
  tick: number;
  /** Latest note-onset tick in this cluster */
  maxOnsetTick: number;
  /** End tick of the note cluster, truncated at the next row */
  endTick: number;
  type: "single" | "multi" | "chord";
  /** All unique pitch classes (0-11) across all hands */
  pitchClasses: number[];
  /** All unique MIDI note numbers across all hands, sorted ascending */
  midis: number[];
  /** Notes grouped by hand/lane. Ordered by lane index. */
  hands: HandNotes[];
  /** Chord name, e.g. "Am7" (only for type === 'chord') */
  chordName?: string;
  /** Pitch class of the chord root (only for type === 'chord') */
  chordRoot?: number;
  /**
   * Fin de la zone harmonique, telle que l'analyse la donne — la borne de
   * l'accord lui-même, là où `endTick` ne borne que la grappe de notes et se
   * fait tronquer par la rangée suivante (une mélodie qui court par-dessus).
   * C'est jusque-là que l'étiquette tient la playhead.
   *
   * Absente sur le piano en direct, qui détecte des accords sans leur
   * connaître de fin : `endTick` y fait office de zone.
   */
  chordEndTick?: number;
  /** Roman numeral, e.g. "I", "vi" (from analyzer API) */
  romanNumeral?: string;
  /** Voicing type, e.g. "arpeggio", "block" (from analyzer API) */
  voicingType?: string;
};

// ── Pool item holding a line + its two text labels ──────────────
type HarmonySlot = {
  line: Graphics;
  /** Width baked into the line geometry; -1 if not yet drawn. */
  lineWidth: number;
  chordText: Text;
  romanText: Text;
};

/** Max opacity for the line closest to the playhead */
const LINE_ALPHA_MAX = 0.5;
/** Min opacity for lines far away */
const LINE_ALPHA_MIN = 0.04;
/** Distance (in seconds) over which opacity fades to minimum */
const FADE_RANGE_SECONDS = 2;

const LABEL_PAD_LEFT = 8;
const LABEL_PAD_ABOVE = 2;
/** Ecart sous la ligne, une fois le libellé passé dessous */
const LABEL_PAD_BELOW = 2;
const ROMAN_GAP = 4;
/** Hauteur du fondu de sortie au bord bas du roll */
const EXIT_FADE_PX = 24;

export class Harmony {
  private container: Container;
  /** Les lignes, sous les notes. Voir le constructeur. */
  private readonly lineContainer: Container;
  private readonly ownsLineContainer: boolean;
  private previousProps?: HarmonyProps;

  // Object pool
  private pool: HarmonySlot[] = [];
  private poolIndex = 0;

  // Pool shrink bookkeeping
  private highWaterMark = 0;
  private framesSinceLastShrink = 0;

  /**
   * Les lignes ne vivent pas au même étage que les étiquettes.
   *
   * Une ligne d'accord traverse tout le roll : au-dessus des notes, elle
   * barrait celles qu'elle croisait — à 0,5 d'opacité, la note avait l'air
   * transparente, ou fendue. Elle passe donc sous elles (`lineStage`, posé
   * avant `Notes` par le rendu), et l'étiquette reste tout en haut : elle
   * porte du texte, elle tient dans la gouttière de gauche, et rien ne doit
   * la couvrir.
   *
   * Sans `lineStage`, tout retombe dans le même conteneur — le comportement
   * d'avant, pour un appelant qui n'aurait pas de place à donner aux lignes.
   */
  constructor(private stage: Container, lineStage?: Container) {
    this.container = new Container();
    this.stage.addChild(this.container);

    if (lineStage === undefined) {
      this.lineContainer = this.container;
      this.ownsLineContainer = false;
    } else {
      this.lineContainer = new Container();
      lineStage.addChild(this.lineContainer);
      this.ownsLineContainer = true;
    }
  }

  destroy(): void {
    for (const slot of this.pool) {
      slot.line.destroy();
      slot.chordText.destroy();
      slot.romanText.destroy();
    }
    this.pool.length = 0;
    this.poolIndex = 0;

    if (this.ownsLineContainer) {
      this.lineContainer.destroy({ children: true });
    }

    this.container.destroy({ children: true });
  }

  draw(props: HarmonyProps): void {
    if (this.shouldSkip(props)) return;

    const { currentTick, currentSeconds, tickToSeconds, fromTick, toTick, width, height, playheadY, hints } = props;
    const pxPerTick = height / (toTick - fromTick);

    this.resetPool();

    // Pre-filter to chord hints only (with indices into the original array)
    const chords: { hint: HintRow; nextTick: number }[] = [];
    for (let i = 0; i < hints.length; i++) {
      const hint = hints[i];
      if (hint.chordRoot === undefined || !hint.chordName) continue;
      chords.push({ hint, nextTick: Infinity });
    }
    // Link each chord to the next chord's tick
    for (let i = 0; i < chords.length - 1; i++) {
      chords[i].nextTick = chords[i + 1].hint.tick;
    }

    /** Y naturelle d'un tick : sa place sur le roll, sans collage. */
    const yOfTick = (tick: number) => height - (tick - fromTick) * pxPerTick;

    for (let i = 0; i < chords.length; i++) {
      const { hint, nextTick } = chords[i];

      // L'accord tient la playhead jusqu'au bout de sa zone à lui, celle que
      // l'analyse lui donne — pas jusqu'à la fin de ses notes, qu'une mélodie
      // par-dessus vient tronquer. `nextTick` ne borne que le cas pathologique
      // de deux zones qui se chevauchent.
      const holdUntil = Math.min(hint.chordEndTick ?? hint.endTick, nextTick);

      /** Tick d'où se mesure l'éloignement à la playhead (opacité). */
      let refTick: number;
      let y: number;

      if (currentTick < hint.tick) {
        // Pas encore arrivé : il descend vers la playhead.
        y = yOfTick(hint.tick);
        refTick = hint.tick;
      } else if (currentTick < holdUntil) {
        // Collé à la playhead tant qu'il dure, et rien ne l'en écarte avant
        // l'heure : quand les deux zones se touchent, sa ligne, celle de
        // l'accord qui arrive et la playhead sont au même endroit à l'instant
        // du relais. Les deux libellés se superposent alors le temps que le
        // sortant s'en aille — une centaine de millisecondes, on laisse faire.
        y = playheadY;
        refTick = currentTick;
      } else {
        // Fini : il reprend sa descente au rythme du roll depuis la playhead,
        // qu'il vient de quitter. Sa place naturelle, elle, est déjà loin sous
        // le bord bas — il a passé toute sa zone collé à la playhead pendant
        // que le roll défilait. Y revenir le ferait disparaître d'un coup ;
        // c'est la fin de sa zone qui le fait sortir par le bas, comme le
        // reste du roll.
        y = yOfTick(holdUntil);
        refTick = holdUntil;
      }

      // Skip if fully outside the visible range
      if (y < 0 || y > height) continue;

      const color = pitchClassColor(hint.chordRoot!);
      const slot = this.acquire();

      // ── Chord name ──────────────────────────────────
      // Posé avant tout le reste : sa hauteur mesurée sert au placement, et son
      // bas décide de la sortie du cadre.
      const ct = slot.chordText;
      ct.text = hint.chordName!;
      ct.style.fill = color;
      ct.x = LABEL_PAD_LEFT;

      // Le libellé se tient au-dessus de sa ligne — sauf quand celui de l'accord
      // qui arrive vient le pousser. La règle tient en une phrase : il ne monte
      // jamais plus haut que la ligne suivante, sous laquelle il se range. Il
      // descend donc à mesure qu'elle approche, et se retrouve exactement sous sa
      // propre ligne à l'instant du relais, où les deux se confondent. Les deux
      // libellés ne se superposent pas pour autant : le sortant passe sous la
      // ligne de l'entrant, l'entrant reste au-dessus d'elle.
      //
      // Passé le relais, `refTick` ne bouge plus : l'écart est figé, le libellé
      // reste sous sa ligne et sort du cadre avec elle.
      const nextLineOffset = -(nextTick - refTick) * pxPerTick;
      const labelOffset = Math.max(-(ct.height + LABEL_PAD_ABOVE), nextLineOffset + LABEL_PAD_BELOW);
      ct.y = y + labelOffset;

      // Opacité selon la distance à la playhead (en secondes), mesurée sur la
      // position affichée et non sur le tick : un accord tenu reste vif tant
      // qu'il l'est, et ne commence à pâlir qu'une fois lâché.
      const distSeconds = Math.abs(tickToSeconds(refTick) - currentSeconds);
      const t = Math.min(distSeconds / FADE_RANGE_SECONDS, 1);
      let alpha = LINE_ALPHA_MAX + (LINE_ALPHA_MIN - LINE_ALPHA_MAX) * t;

      // Fondu sur les derniers pixels, pour que rien ne se coupe net au bord bas
      // du roll (aucun masque sur la scène : elle ne peut pas déborder sur le
      // clavier, elle s'éteint donc juste avant). Mesuré sur le bas de ce qui
      // est dessiné : le libellé passé sous sa ligne sort avant elle.
      const exitEdge = Math.max(y, ct.y + ct.height);

      if (exitEdge > height - EXIT_FADE_PX) {
        alpha *= Math.max(0, (height - exitEdge) / EXIT_FADE_PX);
      }

      // ── Line ────────────────────────────────────────
      // Geometry is baked once per width; only transform/tint/alpha
      // change per frame to avoid rebuilding stroke triangulation
      // (Pixi's buildLine was the dominant per-frame allocator here).
      if (slot.lineWidth !== width) {
        slot.line.clear();
        slot.line.moveTo(0, 0);
        slot.line.lineTo(width, 0);
        slot.line.stroke({ color: 0xffffff, alpha: 1, width: 1 });
        slot.lineWidth = width;
      }

      slot.line.tint = color;
      slot.line.alpha = alpha;
      slot.line.x = 0;
      slot.line.y = y;
      slot.line.visible = true;

      ct.alpha = alpha * 2.5;  // labels slightly more visible than the line
      ct.visible = true;

      // ── Roman numeral ─────────────────────────────────────
      if (hint.romanNumeral) {
        const rt = slot.romanText;
        rt.text = hint.romanNumeral;
        rt.style.fill = color;
        rt.x = ct.x + ct.width + ROMAN_GAP;
        rt.y = ct.y + (ct.height - rt.height);  // align baselines
        rt.alpha = alpha * 1.5;
        rt.visible = true;
      } else {
        slot.romanText.visible = false;
      }
    }

    this.hideUnused();

    // Track high-water mark and periodically shrink the pool
    if (this.poolIndex > this.highWaterMark) {
      this.highWaterMark = this.poolIndex;
    }
    this.framesSinceLastShrink++;
    if (this.framesSinceLastShrink >= 300) {
      this.shrinkPool();
      this.framesSinceLastShrink = 0;
    }
  }

  // ── Pool management ─────────────────────────────────────────────

  private acquire(): HarmonySlot {
    if (this.poolIndex < this.pool.length) {
      return this.pool[this.poolIndex++];
    }

    const line = new Graphics();

    const chordText = new Text({
      text: "",
      style: new TextStyle({
        fontSize: 16,
        fontWeight: "700",
        fontFamily: "Inter, sans-serif",
        fill: 0xffffff,
        dropShadow: {
          color: 0x000000,
          blur: 6,
          distance: 0,
          alpha: 0.8,
        },
      }),
    });

    const romanText = new Text({
      text: "",
      style: new TextStyle({
        fontSize: 12,
        fontWeight: "400",
        fontStyle: "italic",
        fontFamily: "Inter, sans-serif",
        fill: 0xffffff,
        dropShadow: {
          color: 0x000000,
          blur: 4,
          distance: 0,
          alpha: 0.6,
        },
      }),
    });

    chordText.visible = false;
    romanText.visible = false;

    this.lineContainer.addChild(line);
    this.container.addChild(chordText);
    this.container.addChild(romanText);

    const slot: HarmonySlot = { line, lineWidth: -1, chordText, romanText };
    this.pool.push(slot);
    this.poolIndex++;

    return slot;
  }

  private resetPool(): void {
    this.poolIndex = 0;
  }

  private hideUnused(): void {
    for (let i = this.poolIndex; i < this.pool.length; i++) {
      this.pool[i].line.visible = false;
      this.pool[i].chordText.visible = false;
      this.pool[i].romanText.visible = false;
    }
  }

  private shrinkPool(): void {
    const targetSize = Math.ceil(this.highWaterMark * 1.2);
    if (this.pool.length > targetSize) {
      for (let i = targetSize; i < this.pool.length; i++) {
        this.pool[i].line.destroy();
        this.pool[i].chordText.destroy();
        this.pool[i].romanText.destroy();
      }
      this.pool.length = targetSize;
    }
    this.highWaterMark = 0;
  }

  private shouldSkip(props: HarmonyProps): boolean {
    const prev = this.previousProps;
    // Always store current props for next comparison
    this.previousProps = props;

    if (!prev) return false;

    return prev.fromTick === props.fromTick
      && prev.toTick === props.toTick
      && prev.currentTick === props.currentTick
      && prev.width === props.width
      && prev.height === props.height
      && prev.playheadY === props.playheadY
      && prev.hints === props.hints;
  }
}

